import { z } from 'zod';

/**
 * Customer validation schema
 * Matches backend StoreCustomerRequest validation rules
 */
export const customerSchema = z.object({
  // Customer type - matches backend: 'individual' or 'business'
  customer_type: z.enum(['individual', 'business']),
  
  // Basic information - backend uses first_name/last_name
  first_name: z.string().min(1, 'First name is required').max(255),
  last_name: z.string().min(1, 'Last name is required').max(255),
  company_name: z.string().max(255).optional(),
  
  // Contact information
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone: z.string().max(20).optional(),
  mobile: z.string().max(20).optional(),
  alternative_phone: z.string().max(20).optional(),
  fax: z.string().max(20).optional(),
  
  // Address (single address structure)
  address: z.string().max(500).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  postal_code: z.string().max(20).optional(),
  country: z.string().max(100).optional(),
  
  // Business fields
  tax_id: z.string().max(50).optional(),
  business_license: z.string().max(100).optional(),
  
  // Location
  preferred_location_id: z.string().length(26, 'Preferred location is invalid').optional(),

  // Ownership — optional salesperson reassignment (secondary path; primary is the
  // detail-page picker). Mirrors preferred_location_id's nullable/optional shape.
  owner_id: z.string().nullable().optional(),

  // Additional
  notes: z.string().max(2000).optional(),
  status: z.enum(['active', 'inactive', 'lead']).optional(),
  preferences: z.record(z.string(), z.any()).optional(),
})
.refine(
  (data) => {
    // If customer type is 'business', company_name should be provided
    if (data.customer_type === 'business' && !data.company_name) {
      return false;
    }
    return true;
  },
  {
    message: 'Company name is required for business customers',
    path: ['company_name'],
  }
);

export type CustomerFormData = z.infer<typeof customerSchema>;

/**
 * Quick create schema (minimal fields for LeadFormSheet quick create)
 */
export const quickCustomerSchema = z.object({
  customer_type: z.enum(['individual', 'business']),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  company_name: z.string().optional(),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone: z.string().optional(),
}).refine(
  (data) => {
    if (data.customer_type === 'business' && !data.company_name) {
      return false;
    }
    return true;
  },
  {
    message: 'Company name is required for business customers',
    path: ['company_name'],
  }
);

export type QuickCustomerFormData = z.infer<typeof quickCustomerSchema>;
